import { supabase } from '@/utils/supabase/client';

// ─── TypeScript types ───────────────────────────────────────────────────────

export interface AqlOption {
  id: string;
  name: string;
  code?: string | null;
}

export interface AqlCohort {
  id: string;
  name: string | null;
  species_id: string | null;
  phase_id: string | null;
  zone_id: string | null;
  project_id: string | null;
  location_id: string | null;
  client_id: string | null;
  start_date: string | null;
}

export interface AqlGrowthMetric {
  id: string;
  cohort_id: string | null;
  zone_id: string | null;
  metric_day: string;
  median_size: number | null;
  avg_size: number | null;
  p10_size: number | null;
  p25_size: number | null;
  p75_size: number | null;
  p90_size: number | null;
  sample_count: number | null;
  rejected_count: number | null;
  confidence: number | null;
  uniformity_score: number | null;
  warning_level: GrowthWarningLevel | null;
  has_enough_sample: boolean | null;
}

export interface AqlGrowthMeasurement {
  id: string;
  cohort_id: string | null;
  zone_id: string | null;
  camera_id: string | null;
  capture_id: string | null;
  detection_id: string | null;
  measured_at: string;
  length_mm: number | null;
  width_mm: number | null;
  area_mm2: number | null;
  estimated_weight_g: number | null;
  depth_value: number | null;
  thickness_mm: number | null;
  volume_cm3: number | null;
  weight_uncertainty_g: number | null;
  actual_weight_g: number | null;
  model_version: string | null;
  depth_model_version: string | null;
  detection_confidence: number | null;
  measurement_quality: number | null;
  is_valid: boolean;
  rejection_reason: string | null;
  created_at?: string | null;
}

export interface AqlGrowthCalibration {
  id: string;
  zone_id: string | null;
  camera_id: string | null;
  pixel_to_mm_ratio: number | null;
  calibration_type: string | null;
  position_code: string | null;
  qr_code: string | null;
  quality_score: number | null;
  valid_from: string | null;
  valid_to: string | null;
  is_active: boolean | null;
  depth_model_name: string | null;
  depth_model_version: string | null;
  reference_depth: number | null;
  measurement_profiles: Record<string, unknown> | null;
}

export type GrowthWarningLevel = 'normal' | 'warning' | 'critical';

export interface AqlGrowthDashboardPoint {
  cohort_id: string | null;
  zone_id: string | null;
  period_start: string;
  period_end: string | null;
  median_size: number | null;
  avg_size: number | null;
  p10_size: number | null;
  p25_size: number | null;
  p75_size: number | null;
  p90_size: number | null;
  sample_count: number | null;
  rejected_count: number | null;
  confidence: number | null;
  uniformity_score: number | null;
  warning_level: GrowthWarningLevel | null;
  has_enough_sample: boolean | null;
}

export interface AqlVisionCapture {
  id: string;
  zone_id: string | null;
  cohort_id: string | null;
  image_url: string | null;
  captured_at: string;
  sample_count?: number | null;
  median_size?: number | null;
}

export interface GrowthFilters {
  clientId?: string;
  projectId?: string;
  locationId?: string;
  zoneId?: string;
  cohortId?: string;
  speciesId?: string;
  phaseId?: string;
  /** Inclusive ISO date (yyyy-mm-dd) — start of range. */
  fromDate: string;
  /** Inclusive ISO date (yyyy-mm-dd) — end of range. */
  toDate: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const handleQueryError = <T,>(result: { data: unknown; error: unknown }, context: string): T => {
  if (result.error) {
    const message = result.error instanceof Error ? result.error.message : String(result.error);
    throw new Error(`${context}: ${message}`);
  }
  return (result.data ?? []) as unknown as T;
};

const applyOptionalEq = <Q extends { eq: (column: string, value: string) => Q }>(
  query: Q,
  column: string,
  value: string | undefined
): Q => (value ? query.eq(column, value) : query);

const toIsoDate = (date: Date): string => date.toISOString().slice(0, 10);

export const isoDaysAgo = (days: number): string => {
  const date = new Date();
  date.setUTCHours(0, 0, 0, 0);
  date.setUTCDate(date.getUTCDate() - days);
  return toIsoDate(date);
};

export const todayIso = (): string => {
  const date = new Date();
  date.setUTCHours(0, 0, 0, 0);
  return toIsoDate(date);
};

// ─── Filter sources ─────────────────────────────────────────────────────────

export async function fetchClients(): Promise<AqlOption[]> {
  const result = await supabase
    .from('aql_clients')
    .select('id:client_id, name')
    .order('name', { ascending: true });
  return handleQueryError(result, 'Failed to load clients') as AqlOption[];
}

export async function fetchProjects(clientId?: string): Promise<AqlOption[]> {
  let query = supabase.from('aql_projects').select('id:project_id, name').order('name', { ascending: true });
  query = applyOptionalEq(query, 'client_id', clientId);
  return handleQueryError(await query, 'Failed to load projects') as AqlOption[];
}

export async function fetchLocations(clientId?: string, projectId?: string): Promise<AqlOption[]> {
  let query = supabase.from('aql_locations').select('id:location_id, name').order('name', { ascending: true });
  query = applyOptionalEq(query, 'client_id', clientId);
  query = applyOptionalEq(query, 'project_id', projectId);
  return handleQueryError(await query, 'Failed to load locations') as AqlOption[];
}

export async function fetchZones(filters: {
  clientId?: string;
  projectId?: string;
  locationId?: string;
}): Promise<AqlOption[]> {
  let query = supabase.from('aql_zones').select('id:zone_id, name, code').order('name', { ascending: true });
  query = applyOptionalEq(query, 'client_id', filters.clientId);
  query = applyOptionalEq(query, 'project_id', filters.projectId);
  query = applyOptionalEq(query, 'location_id', filters.locationId);
  return handleQueryError(await query, 'Failed to load zones') as AqlOption[];
}

export async function fetchCohorts(filters: {
  clientId?: string;
  projectId?: string;
  locationId?: string;
  zoneId?: string;
  speciesId?: string;
  phaseId?: string;
}): Promise<AqlCohort[]> {
  let query = supabase
    .from('aql_cohorts')
    .select('id, name, species_id, phase_id, zone_id, project_id, location_id, client_id, start_date')
    .order('start_date', { ascending: false });
  query = applyOptionalEq(query, 'client_id', filters.clientId);
  query = applyOptionalEq(query, 'project_id', filters.projectId);
  query = applyOptionalEq(query, 'location_id', filters.locationId);
  query = applyOptionalEq(query, 'zone_id', filters.zoneId);
  query = applyOptionalEq(query, 'species_id', filters.speciesId);
  query = applyOptionalEq(query, 'phase_id', filters.phaseId);
  return handleQueryError(await query, 'Failed to load cohorts') as AqlCohort[];
}

export async function fetchSpecies(): Promise<AqlOption[]> {
  const result = await supabase
    .from('aql_species')
    .select('id:species_id, name:common_name, code')
    .order('common_name', { ascending: true });
  return handleQueryError(result, 'Failed to load species') as AqlOption[];
}

export async function fetchPhases(): Promise<AqlOption[]> {
  const result = await supabase
    .from('aql_phases')
    .select('id:phase_id, name, code')
    .order('name', { ascending: true });
  return handleQueryError(result, 'Failed to load phases') as AqlOption[];
}

// ─── Dashboard series (main time-series — aggregated) ───────────────────────

export async function fetchDashboardSeries(filters: GrowthFilters): Promise<AqlGrowthDashboardPoint[]> {
  const selectColumns = [
    'cohort_id',
    'zone_id',
    'period_start',
    'period_end',
    'median_size',
    'avg_size',
    'p10_size',
    'p25_size',
    'p75_size',
    'p90_size',
    'sample_count',
    'rejected_count',
    'confidence',
    'uniformity_score',
    'warning_level',
    'has_enough_sample'
  ].join(', ');

  const runQuery = async (includeScopeFilters: boolean) => {
    let query = supabase
      .from('aql_growth_dashboard_series')
      .select(selectColumns)
      .gte('period_start', filters.fromDate)
      .lte('period_start', filters.toDate)
      .order('period_start', { ascending: true });

    if (includeScopeFilters) {
      query = applyOptionalEq(query, 'client_id', filters.clientId);
      query = applyOptionalEq(query, 'project_id', filters.projectId);
      query = applyOptionalEq(query, 'location_id', filters.locationId);
      query = applyOptionalEq(query, 'species_id', filters.speciesId);
      query = applyOptionalEq(query, 'phase_id', filters.phaseId);
    }

    query = applyOptionalEq(query, 'cohort_id', filters.cohortId);
    query = applyOptionalEq(query, 'zone_id', filters.zoneId);

    return query;
  };

  let result = await runQuery(true);
  const hasScopeFilters = Boolean(filters.clientId || filters.projectId || filters.locationId || filters.speciesId || filters.phaseId);
  const errorMessage = result.error instanceof Error ? result.error.message : String(result.error ?? '');
  if (hasScopeFilters && result.error && /schema cache|column .* does not exist|could not find/i.test(errorMessage)) {
    result = await runQuery(false);
  }

  return handleQueryError(result, 'Failed to load growth series') as AqlGrowthDashboardPoint[];
}

// ─── Raw measurements (latest distribution + table) ─────────────────────────

export async function fetchLatestDayMeasurements(filters: {
  cohortId?: string;
  zoneId?: string;
  /** ISO date (yyyy-mm-dd). When omitted, returns the latest day with data. */
  day?: string;
  limit?: number;
}): Promise<AqlGrowthMeasurement[]> {
  if (!filters.cohortId && !filters.zoneId) return [];

  const dayStart = filters.day ? `${filters.day}T00:00:00.000Z` : undefined;
  const dayEnd = filters.day ? `${filters.day}T23:59:59.999Z` : undefined;

  let query = supabase
    .from('aql_growth_measurements')
    .select(
      [
        'id',
        'cohort_id',
        'zone_id',
        'camera_id',
        'capture_id',
        'detection_id',
        'measured_at',
        'length_mm',
        'width_mm',
        'area_mm2',
        'estimated_weight_g',
        'depth_value',
        'thickness_mm',
        'volume_cm3',
        'weight_uncertainty_g',
        'actual_weight_g',
        'model_version',
        'depth_model_version',
        'detection_confidence',
        'measurement_quality',
        'is_valid',
        'rejection_reason'
      ].join(', ')
    )
    .order('measured_at', { ascending: false })
    .limit(filters.limit ?? 1000);

  query = applyOptionalEq(query, 'cohort_id', filters.cohortId);
  query = applyOptionalEq(query, 'zone_id', filters.zoneId);
  if (dayStart) query = query.gte('measured_at', dayStart);
  if (dayEnd) query = query.lte('measured_at', dayEnd);

  return handleQueryError(await query, 'Failed to load measurements') as AqlGrowthMeasurement[];
}

export async function fetchRecentMeasurements(filters: {
  cohortId?: string;
  zoneId?: string;
  limit?: number;
}): Promise<AqlGrowthMeasurement[]> {
  if (!filters.cohortId && !filters.zoneId) return [];

  let query = supabase
    .from('aql_growth_measurements')
    .select(
      [
        'id',
        'cohort_id',
        'zone_id',
        'camera_id',
        'capture_id',
        'detection_id',
        'measured_at',
        'length_mm',
        'width_mm',
        'area_mm2',
        'estimated_weight_g',
        'depth_value',
        'thickness_mm',
        'volume_cm3',
        'weight_uncertainty_g',
        'actual_weight_g',
        'model_version',
        'depth_model_version',
        'detection_confidence',
        'measurement_quality',
        'is_valid',
        'rejection_reason'
      ].join(', ')
    )
    .order('measured_at', { ascending: false })
    .limit(filters.limit ?? 200);

  query = applyOptionalEq(query, 'cohort_id', filters.cohortId);
  query = applyOptionalEq(query, 'zone_id', filters.zoneId);

  return handleQueryError(await query, 'Failed to load measurements') as AqlGrowthMeasurement[];
}

// ─── Calibration ────────────────────────────────────────────────────────────

export async function fetchActiveCalibration(filters: {
  zoneId?: string;
  cameraId?: string;
}): Promise<AqlGrowthCalibration | null> {
  if (!filters.zoneId && !filters.cameraId) return null;

  let query = supabase
    .from('aql_growth_calibrations')
    .select(
      [
        'id',
        'zone_id',
        'camera_id',
        'pixel_to_mm_ratio',
        'calibration_type',
        'position_code',
        'qr_code',
        'quality_score',
        'valid_from',
        'valid_to',
        'is_active'
        ,'depth_model_name'
        ,'depth_model_version'
        ,'reference_depth'
        ,'measurement_profiles'
      ].join(', ')
    )
    .eq('is_active', true)
    .order('valid_from', { ascending: false })
    .limit(1);

  query = applyOptionalEq(query, 'zone_id', filters.zoneId);
  query = applyOptionalEq(query, 'camera_id', filters.cameraId);

  const result = await query;
  if (result.error) throw new Error(`Failed to load calibration: ${result.error.message ?? result.error}`);
  const rows = (result.data ?? []) as unknown as AqlGrowthCalibration[];
  return rows[0] ?? null;
}

// ─── Captures ───────────────────────────────────────────────────────────────

export async function fetchRecentCaptures(filters: {
  zoneId?: string;
  cohortId?: string;
  limit?: number;
}): Promise<AqlVisionCapture[]> {
  if (!filters.zoneId && !filters.cohortId) return [];

  let query = supabase
    .from('aql_vision_captures')
    .select('id, zone_id, cohort_id, image_url, captured_at, sample_count, median_size')
    .order('captured_at', { ascending: false })
    .limit(filters.limit ?? 8);

  query = applyOptionalEq(query, 'zone_id', filters.zoneId);
  query = applyOptionalEq(query, 'cohort_id', filters.cohortId);

  return handleQueryError(await query, 'Failed to load captures') as AqlVisionCapture[];
}

// ─── RPC: refresh aggregated metrics for one day ────────────────────────────

export async function refreshGrowthMetricDay(params: {
  cohortId?: string;
  zoneId?: string;
  /** ISO date string (yyyy-mm-dd). Defaults to today. */
  day?: string;
}): Promise<void> {
  const payload: Record<string, string> = {
    p_day: params.day ?? todayIso()
  };
  if (params.cohortId) payload.p_cohort_id = params.cohortId;
  if (params.zoneId) payload.p_zone_id = params.zoneId;

  const result = await supabase.rpc('aql_refresh_growth_metric_day', payload);
  if (result.error) {
    throw new Error(`Failed to refresh growth metrics: ${result.error.message ?? result.error}`);
  }
}
